import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { getCompanyLogo } from 'lib/api/company';
import {
  checkReverseConciliation,
  getPayInConciliations,
  reverseDocumentConciliation,
} from 'lib/api/conciliations';
import { expressGetCompanyLogo } from 'lib/api/express';
import CustomButton from 'lib/designSystem/Button';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import {
  hasAnyPermissions,
  loggerDev,
  showAlert,
  validateProducts,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import type { PayInExportConfig } from '../../../../../../../Financing/common/exportPayInReport';
import {
  generatePayInReportPDF,
  PayInExportDialog,
} from '../../../../../../../Financing/common/exportPayInReport';

const PaymentConciliationAssginationDone = (props: any) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const { payIn, bigSetup, setOpen } = props;
  const [documentConciliations, setDocumentConciliations] = useState<any[]>([]);
  const [fncConciliations, setFncConciliations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [openWarning, setOpenWarning] = useState(false);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [canReverseConciliation, setCanReverseConciliation] = useState(false);
  const [cantReverseReason, setCantReverseReason] = useState('');
  const [payInConciliation, setPayInConciliation] = useState<null | number>(
    null,
  );
  const [openExportDialog, setOpenExportDialog] = useState(false);

  const dispatch = useDispatch();

  const setUp = async () => {
    setLoading(true);
    try {
      const response = await getPayInConciliations(dispatch, {
        id: payIn.id,
      });
      setDocumentConciliations(response.documentConciliations);
      setFncConciliations(response.fncConciliations);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
  };

  const unDoConciliation = async () => {
    if (!payInConciliation) return;
    setLoading(true);
    try {
      setOpenWarning(false);
      setOpen(true);
      await reverseDocumentConciliation(dispatch, payInConciliation);
      await bigSetup();
      await setUp();
    } catch (error) {
      loggerDev(error);
      showAlert(dispatch, 'error', 'Error al reversar la conciliación');
    }
    setLoading(false);
  };

  const openConfirmConciliation = async (payInConciliationId: number) => {
    setPayInConciliation(payInConciliationId);
    setLoading(true);
    try {
      const response = await checkReverseConciliation(
        dispatch,
        payInConciliationId,
      );
      setWarningMessages(response.messages);
      setCanReverseConciliation(response.canReverse);
      setCantReverseReason(response.cantReverseReason);
    } catch (error) {
      loggerDev(error);
    }
    setLoading(false);
    setOpenWarning(true);
  };

  const handleOpenExportDialog = () => {
    setOpenExportDialog(true);
  };

  const handleDownloadReport = async (exportConfig?: PayInExportConfig) => {
    setLoading(true);
    setOpenExportDialog(false);
    try {
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);
      await generatePayInReportPDF(
        payIn,
        documentConciliations,
        fncConciliations,
        user?.name || '',
        imageBase64,
        exportConfig,
      );
    } catch (error) {
      console.error(error);
      loggerDev(error);
    }
    setLoading(false);
  };

  const canReverse = () =>
    hasAnyPermissions(user, ['financing_payIn_conciliate_reverse']);

  const actions = [
    {
      action: openConfirmConciliation,
      actionLabel: 'Reversar',
      disabled: !canReverse,
    },
  ];

  useEffect(() => {
    void setUp();
  }, []);

  const buttons: CustomButtonProps[] = [
    {
      color: 'primary',
      action: () => handleOpenExportDialog(),
      name: 'Exportar',
    },
  ];

  return (
    <div>
      <HeaderWithActions
        title="Documentos"
        variant="h6"
        buttons={loading ? [] : buttons}
        fontWeight="bold"
      />
      {loading ? (
        <CircularProgress />
      ) : (
        <div>
          <Typography variant="body1" padding="10px 0px" fontWeight={600}>
            Documentos conciliados en este pago
          </Typography>
          {documentConciliations.length ? (
            <EnhancedTable
              dataHeaders={[
                { key: 'folio', label: 'Folio' },
                { key: 'amount', label: 'Monto', type: 'money' },
                { key: 'amountNet', label: 'Monto neto', type: 'money' },
                { key: 'advance', label: 'Avance' },
                {
                  key: 'createdAt',
                  label: 'Fecha conciliación',
                  type: 'date',
                },
                {
                  key: 'observation',
                  label: 'Observación conciliación',
                },
                {
                  key: 'amountConciliated',
                  label: 'Monto conciliado',
                  type: 'money',
                },
              ]}
              data={documentConciliations}
              actions={actions}
              exportToFile="xlsx"
            />
          ) : (
            <Typography variant="body1">
              No hay conciliaciones en este pago
            </Typography>
          )}
          {!validateProducts(user, ['sgr']) && (
            <>
              <Typography variant="body1" padding="10px 0px" fontWeight={600}>
                FNC conciliado en este pago
              </Typography>
              {fncConciliations.length ? (
                <div>
                  <EnhancedTable
                    dataHeaders={[
                      { key: 'id', label: 'Folio', type: 'numeric-id' },
                      { key: 'amount', label: 'Monto', type: 'money' },
                      {
                        key: 'createdAt',
                        label: 'Fecha de conciliación',
                        type: 'date',
                      },
                      {
                        key: 'amount',
                        label: 'Monto conciliado',
                        type: 'money',
                      },
                    ]}
                    data={fncConciliations}
                  />
                </div>
              ) : (
                <Typography variant="body1">
                  No hay conciliaciones en este pago
                </Typography>
              )}
            </>
          )}
        </div>
      )}
      <Dialog maxWidth="md" open={openWarning}>
        <DialogTitle>Reversar conciliación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro que quieres reversar esta conciliación? Se ejecutarán
            las siguientes acciones:
          </Typography>
          <div style={{ padding: '10px 0px' }}>
            {warningMessages.map((message) => (
              <Typography key={message} variant="body1" color="error">
                - {message}
              </Typography>
            ))}
          </div>
          {!canReverseConciliation && cantReverseReason && (
            <div style={{ padding: '10px 0px', marginTop: '10px' }}>
              <Typography variant="body1" color="warning.main">
                {cantReverseReason}
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpenWarning(false)}>
            Salir
          </CustomButton>
          <CustomButton
            disabled={!canReverse() || !canReverseConciliation}
            onClick={() => unDoConciliation()}
            color="error"
          >
            Reversar
          </CustomButton>
        </DialogActions>
      </Dialog>
      <PayInExportDialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        onConfirm={handleDownloadReport}
      />
    </div>
  );
};

export default PaymentConciliationAssginationDone;
